import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { FaIcon } from 'client/js/util/layout_utils';
import Callout from 'client/js/util/callout';
import { useFileList, useMoveItem, useCopyItem, useDeleteItem, useRenameItem } from './hooks/use_filemanager';
import FileList from './file_list';
import Toolbar from './toolbar';
import UploadModal from './upload_modal';
import CreateItemModal from './create_item_modal';
import RenameModal from './rename_modal';
import ChmodModal from './chmod_modal';
import ContextMenu from './context_menu';

const FileBrowser = ({ paneId, className = '', sharedClipboard, setSharedClipboard, baseApiUrl }) => {
  const [currentPath, setCurrentPath] = useState('/');
  const [selectedItems, setSelectedItems] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showChmodModal, setShowChmodModal] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragOperation, setDragOperation] = useState(null); // 'move' or 'copy'
  const [dragItemCount, setDragItemCount] = useState(0);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(-1);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadPath, setUploadPath] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const browserRef = useRef(null);

  const { data, isLoading, error } = useFileList(currentPath, baseApiUrl);
  const moveItemMutation = useMoveItem(baseApiUrl);
  const copyItemMutation = useCopyItem(baseApiUrl);
  const deleteItemMutation = useDeleteItem(baseApiUrl);
  const renameItemMutation = useRenameItem(baseApiUrl);

  // ... (Focus effect) ...

  const handleRenameSubmit = useCallback((item, newName) => {
    setItemToEdit(null); // Stop editing
    
    if (newName && newName !== item.name) {
      renameItemMutation.mutate({
        path: item.path,
        newName,
        currentPath
      }, {
        onSuccess: () => {
          if (browserRef.current) {
            browserRef.current.focus();
          }
        }
      });
    } else {
        // If no rename actually happens (e.g., name is same), still refocus
        if (browserRef.current) {
            browserRef.current.focus();
        }
    }
  }, [renameItemMutation, currentPath, browserRef]);

  const handleRenameCancel = useCallback(() => {
    setItemToEdit(null);
    // Refocus the browser to restore keyboard navigation
    if (browserRef.current) {
      browserRef.current.focus();
    }
  }, []);

  const sortedFiles = useMemo(() => {
    if (!data?.files) return [];
    const files = [...data.files];

    return files.sort((a, b) => {
      // Always keep directories on top
      if (a.type !== b.type) {
        return a.type === 'directory' ? -1 : 1;
      }

      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Case insensitive sort for names
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  const createProgressToast = useCallback((current, total, operation) => {
    const progressPercentage = total > 0 ? Math.round((current / total) * 100) : 0;
    const operationText = operation === 'copy' ? 'Kopiere' : operation === 'move' ? 'Verschiebe' : 'Lösche';

    return (
      <div>
        <div className="mb-2">
          <strong>{operationText}...</strong>
        </div>
        <div className="progress mb-2" style={{ height: '8px' }}>
          <div
            className="progress-bar progress-bar-striped progress-bar-animated"
            role="progressbar"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        <small>{current} / {total} Elemente</small>
      </div>
    );
  }, []);

  const handleDelete = useCallback(async () => {
    if (selectedItems.length === 0) return;

    if (!confirm(`Möchten Sie ${selectedItems.length} Element(e) wirklich löschen?`)) {
      return;
    }

    // Show progress toast for multiple items
    if (selectedItems.length > 1) {
      const toastId = toast.info(createProgressToast(0, selectedItems.length, 'delete'), {
        autoClose: false,
        closeButton: false,
        closeOnClick: false,
        draggable: false,
      });

      let completed = 0;

      try {
        for (let i = 0; i < selectedItems.length; i++) {
          const item = selectedItems[i];
          const skipRefresh = i < selectedItems.length - 1; // Only refresh on last item

          await deleteItemMutation.mutateAsync({
            path: item.path,
            currentPath: currentPath,
            skipRefresh,
            skipToast: true
          });

          completed++;
          toast.update(toastId, {
            render: createProgressToast(completed, selectedItems.length, 'delete')
          });
        }

        toast.update(toastId, {
          render: `${completed} Elemente gelöscht`,
          type: 'success',
          autoClose: 3000,
          closeButton: true,
          draggable: true
        });
        setSelectedItems([]);
      } catch (error) {
        toast.update(toastId, {
          render: 'Fehler beim Löschen der Elemente',
          type: 'error',
          autoClose: 5000
        });
      }
    } else {
      // Single item - use normal mutation without progress toast
      deleteItemMutation.mutate({
        path: selectedItems[0].path,
        currentPath: currentPath,
      }, {
        onSuccess: () => setSelectedItems([])
      });
    }
  }, [selectedItems, currentPath, deleteItemMutation, createProgressToast]);

  const handlePaste = useCallback(async () => {
    if (!sharedClipboard || sharedClipboard.items.length === 0) return;

    const items = sharedClipboard.items;
    const operation = sharedClipboard.operation;

    // Filter out items that are being pasted into their own parent directory (no-op)
    const validItems = items.filter(item => {
      const parentPath = item.path.substring(0, item.path.lastIndexOf('/')) || '/';
      // For move: always no-op
      // For copy: also no-op based on user request, or potentially duplicating? 
      // Requirement says "should be a no-op".
      return parentPath !== currentPath;
    });

    if (validItems.length === 0) {
      // Nothing to do
      if (operation === 'cut') {
         setSharedClipboard(null);
      }
      return;
    }

    // Show progress toast for multiple items
    if (validItems.length > 1) {
      const toastId = toast.info(createProgressToast(0, validItems.length, operation), {
        autoClose: false,
        closeButton: false,
        closeOnClick: false,
        draggable: false,
      });

      let completed = 0;

      try {
        for (let i = 0; i < validItems.length; i++) {
          const item = validItems[i];
          const skipRefresh = i < validItems.length - 1; // Only refresh on last item

          if (operation === 'copy') {
            await copyItemMutation.mutateAsync({
              sourcePath: item.path,
              destinationPath: currentPath,
              skipRefresh,
              skipToast: true
            });
          } else if (operation === 'cut') {
            await moveItemMutation.mutateAsync({
              sourcePath: item.path,
              destinationPath: currentPath,
              currentPath: currentPath,
              skipRefresh,
              skipToast: true
            });
          }

          completed++;
          toast.update(toastId, {
            render: createProgressToast(completed, validItems.length, operation)
          });
        }

        const operationText = operation === 'copy' ? 'kopiert' : 'verschoben';
        toast.update(toastId, {
          render: `${completed} Elemente ${operationText}`,
          type: 'success',
          autoClose: 3000,
          closeButton: true,
          draggable: true
        });
      } catch (error) {
        toast.update(toastId, {
          render: 'Fehler beim Verarbeiten der Elemente',
          type: 'error',
          autoClose: 5000
        });
      }
    } else {
      // Single item - use normal mutation without progress toast
      const item = validItems[0];
      if (operation === 'copy') {
        copyItemMutation.mutate({
          sourcePath: item.path,
          destinationPath: currentPath,
        });
      } else if (operation === 'cut') {
        moveItemMutation.mutate({
          sourcePath: item.path,
          destinationPath: currentPath,
          currentPath: currentPath,
        });
      }
    }

    if (operation === 'cut') {
      setSharedClipboard(null);
    }
  }, [sharedClipboard, currentPath, copyItemMutation, moveItemMutation, createProgressToast, setSharedClipboard]);

  const handleExternalFileDrop = useCallback((files, targetPath) => {
    setUploadFiles(files);
    setUploadPath(targetPath);
    setShowUploadModal(true);
  }, []);

  const handleBrowserClick = useCallback((e) => {
    // Prevent stealing focus from interactive elements
    if (e.target.closest('button') || e.target.closest('input') || e.target.closest('a')) {
      return;
    }
    
    if (browserRef.current) {
      browserRef.current.focus();
    }
  }, []);

  const handleContextMenu = useCallback((e, item) => {
    e.preventDefault();
    
    // If item is null, it's a background click
    if (!item) {
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        item: null
      });
      return;
    }
    
    // Select the item if not already selected
    if (!selectedItems.some(i => i.path === item.path)) {
      setSelectedItems([item]);
      setLastSelectedIndex(sortedFiles.indexOf(item));
    }
    
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      item: item
    });
  }, [selectedItems, sortedFiles]);

  const getContextMenuItems = () => {
    if (!contextMenu) return [];
    
    const item = contextMenu.item;

    // Background context menu (no item selected)
    if (!item) {
      const pasteLabel = sharedClipboard && sharedClipboard.items.length > 0
        ? `Einfügen (${sharedClipboard.items.length} Element${sharedClipboard.items.length !== 1 ? 'e' : ''})`
        : 'Einfügen';

      const items = [
        {
          label: pasteLabel,
          icon: 'fa-paste',
          action: handlePaste,
          disabled: !sharedClipboard || sharedClipboard.items.length === 0
        },
        'separator',
        {
          label: 'Neuer Ordner',
          icon: 'fa-folder-o',
          action: () => setShowCreateModal(true)
        },
        {
          label: 'Upload',
          icon: 'fa-upload',
          action: () => setShowUploadModal(true)
        },
        'separator',
        {
          label: 'Aktualisieren',
          icon: 'fa-refresh',
          action: () => handleNavigate(currentPath)
        }
      ];
      return items;
    }

    // File/Folder context menu
    const items = [
      {
        label: 'Öffnen',
        icon: 'fa-folder-open',
        action: () => handleDoubleClick(item),
        disabled: item.type !== 'directory'
      },
      'separator',
      {
        label: 'Umbenennen',
        icon: 'fa-i-cursor',
        action: () => {
          setItemToEdit(item);
          // setShowRenameModal(true); // Inline renaming
        }
      },
      {
        label: 'Kopieren',
        icon: 'fa-copy',
        action: handleCopy
      },
      {
        label: 'Ausschneiden',
        icon: 'fa-cut',
        action: handleCut
      },
      'separator',
      {
        label: 'Berechtigungen',
        icon: 'fa-lock',
        action: () => {
          setItemToEdit(item);
          setShowChmodModal(true);
        }
      },
      {
        label: 'Herunterladen',
        icon: 'fa-download',
        action: () => {
           const baseUrl = baseApiUrl || '/usercp/filemanager/api';
           window.location.href = `${baseUrl}/download?path=${encodeURIComponent(item.path)}`;
        },
        disabled: item.type === 'directory'
      },
      'separator',
      {
        label: 'Löschen',
        icon: 'fa-trash',
        action: handleDelete
      }
    ];
    
    return items;
  };

  const handleSort = useCallback((key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  const handleNavigate = useCallback((path) => {
    setCurrentPath(path);
    setSelectedItems([]);
    setLastSelectedIndex(-1);
  }, []);

  const handleNavigateUp = useCallback(() => {
    if (currentPath === '/') return;
    const parentPath = currentPath.split('/').slice(0, -1).join('/') || '/';
    handleNavigate(parentPath);
  }, [currentPath, handleNavigate]);

  const handleSelectItem = useCallback((item, isSelected) => {
    if (isSelected) {
      setSelectedItems(prev => [...prev, item]);
    } else {
      setSelectedItems(prev => prev.filter(i => i.path !== item.path));
    }
  }, []);

  const handleItemClick = useCallback((item, index, e) => {
    const files = data?.files || [];

    if (e.ctrlKey || e.metaKey) {
      // Toggle selection
      const isSelected = selectedItems.some(i => i.path === item.path);
      handleSelectItem(item, !isSelected);
      setLastSelectedIndex(index);
    } else if (e.shiftKey) {
      // Range selection
      e.preventDefault(); // Prevent text selection
      if (lastSelectedIndex === -1) {
        setSelectedItems([item]);
        setLastSelectedIndex(index);
      } else {
        const start = Math.min(lastSelectedIndex, index);
        const end = Math.max(lastSelectedIndex, index);
        const rangeItems = files.slice(start, end + 1);
        setSelectedItems(rangeItems);
      }
    } else {
      // Single selection
      setSelectedItems([item]);
      setLastSelectedIndex(index);
    }
  }, [selectedItems, lastSelectedIndex, data, handleSelectItem]);

  const handleCopy = useCallback(() => {
    if (selectedItems.length === 0) return;
    setSharedClipboard({ items: selectedItems, operation: 'copy' });
  }, [selectedItems, setSharedClipboard]);

  const handleCut = useCallback(() => {
    if (selectedItems.length === 0) return;
    setSharedClipboard({ items: selectedItems, operation: 'cut' });
  }, [selectedItems, setSharedClipboard]);

  const handleRename = useCallback(() => {
    if (selectedItems.length !== 1) return;
    setItemToEdit(selectedItems[0]);
    // setShowRenameModal(true); // Removed to use inline renaming
  }, [selectedItems]);

  const handleChmod = useCallback(() => {
    if (selectedItems.length !== 1) return;
    setItemToEdit(selectedItems[0]);
    setShowChmodModal(true);
  }, [selectedItems]);

  const handleDoubleClick = useCallback((item) => {
    if (item.type === 'directory') {
      handleNavigate(item.path);
    }
  }, [handleNavigate]);

  const handlePaneDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    // Try to get drag data to count items
    try {
      const dragDataString = e.dataTransfer.getData('application/json');
      if (dragDataString) {
        const dragData = JSON.parse(dragDataString);
        setDragItemCount(dragData.items?.length || 1);
      }
    } catch (err) {
      // Ignore - data may not be available during dragover
    }

    // Determine operation based on Shift key
    const operation = e.shiftKey ? 'copy' : 'move';
    setDragOperation(operation);
    e.dataTransfer.dropEffect = operation;
    setIsDragOver(true);
  }, []);

  const handlePaneDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setDragOperation(null);
  }, []);

  // Helper to handle batch copy/move from drop events with progress toast
  const processDroppedItems = async (items, targetPath, operation) => {
    if (!items || items.length === 0) return;

    // Filter out items that are being dropped into their own parent directory (no-op)
    const validItems = items.filter(item => {
      const parentPath = item.path.substring(0, item.path.lastIndexOf('/')) || '/';
      return parentPath !== targetPath;
    });

    if (validItems.length === 0) return;

    // Show progress toast
    const toastId = toast.info(createProgressToast(0, validItems.length, operation), {
      autoClose: false,
      closeButton: false,
      closeOnClick: false,
      draggable: false,
    });

    let completed = 0;

    try {
      for (let i = 0; i < validItems.length; i++) {
        const item = validItems[i];
        // Skip refreshing list until the last item
        const skipRefresh = i < validItems.length - 1; 
        // Always skip individual success toasts, we update the main toast
        const skipToast = true;

        if (operation === 'copy') {
          await copyItemMutation.mutateAsync({
            sourcePath: item.path,
            destinationPath: targetPath,
            skipRefresh,
            skipToast
          });
        } else {
          await moveItemMutation.mutateAsync({
            sourcePath: item.path,
            destinationPath: targetPath,
            currentPath: item.path.substring(0, item.path.lastIndexOf('/')) || '/',
            skipRefresh,
            skipToast
          });
        }

        completed++;
        toast.update(toastId, {
          render: createProgressToast(completed, validItems.length, operation)
        });
      }

      const operationText = operation === 'copy' ? 'kopiert' : 'verschoben';
      toast.update(toastId, {
        render: `${completed} Elemente ${operationText}`,
        type: 'success',
        autoClose: 3000,
        closeButton: true,
        draggable: true
      });
    } catch (error) {
      console.error('Drop operation error:', error);
      toast.update(toastId, {
        render: 'Fehler bei der Operation',
        type: 'error',
        autoClose: 5000
      });
    }
  };

  const handlePaneDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    // Check for external files first
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      handleExternalFileDrop(files, currentPath);
      return;
    }

    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
      const { items, sourcePaneId, sourcePath } = dragData;

      // Don't do anything if dropping in the same pane and same directory
      if (sourcePaneId === paneId && sourcePath === currentPath) {
        setDragOperation(null);
        return;
      }

      const operation = e.shiftKey ? 'copy' : 'move';
      
      // Process all items with the helper
      processDroppedItems(items, currentPath, operation);
      
    } catch (err) {
      console.error('Error handling pane drop:', err);
    }

    setDragOperation(null);
  }, [paneId, currentPath, processDroppedItems]);

  const handleItemDrop = useCallback((e, targetItem) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setDragOperation(null);

    // Check for external files first
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      handleExternalFileDrop(files, targetItem.path);
      return;
    }

    try {
      const dragDataString = e.dataTransfer.getData('application/json');
      if (!dragDataString) return;

      const dragData = JSON.parse(dragDataString);
      const { items, sourcePath } = dragData;

      // Avoid moving a folder into itself
      if (items.some(i => i.path === targetItem.path)) {
        return;
      }

      const operation = e.shiftKey ? 'copy' : 'move';
      
      // Process all items with the helper
      processDroppedItems(items, targetItem.path, operation);

    } catch (err) {
      console.error('Error handling item drop:', err);
    }
  }, [processDroppedItems, handleExternalFileDrop]);

  const handleKeyDown = useCallback((e) => {
    if (isLoading || error) return;

    // Ignore if modal is open
    if (showUploadModal || showCreateModal || showRenameModal || showChmodModal) return;

    // Global shortcuts that don't require files
    if (e.key === 'Backspace') {
      e.preventDefault();
      handleNavigateUp();
      return;
    }

    // File-specific shortcuts
    if (e.key === 'Delete') {
      e.preventDefault();
      if (sortedFiles.length > 0) handleDelete();
    } else if (e.key === 'F2') {
      e.preventDefault();
      if (sortedFiles.length > 0) handleRename();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedItems.length === 1) {
        handleDoubleClick(selectedItems[0]);
      }
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      
      if (!sortedFiles.length) return;

      const direction = e.key === 'ArrowDown' ? 1 : -1;
      let nextIndex;

      if (lastSelectedIndex === -1) {
        nextIndex = direction === 1 ? 0 : sortedFiles.length - 1;
      } else {
        nextIndex = lastSelectedIndex + direction;
        if (nextIndex < 0) nextIndex = 0;
        if (nextIndex >= sortedFiles.length) nextIndex = sortedFiles.length - 1;
      }

      const item = sortedFiles[nextIndex];
      if (item) {
        if (e.shiftKey) {
          // Range selection logic (simplified for keyboard)
          // ... (kept simple)
          setSelectedItems([item]);
          setLastSelectedIndex(nextIndex);
        } else {
           setSelectedItems([item]);
           setLastSelectedIndex(nextIndex);
        }
      }
    } else if (e.key === 'a' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (sortedFiles.length > 0) setSelectedItems(sortedFiles);
    }
  }, [isLoading, error, sortedFiles, showUploadModal, showCreateModal, showRenameModal, showChmodModal, handleDelete, handleRename, handleDoubleClick, selectedItems, lastSelectedIndex, handleNavigateUp]);

  const getBreadcrumbs = () => {
    if (currentPath === '/') return [{ name: <FaIcon name="home" />, path: '/' }];

    const parts = currentPath.split('/').filter(Boolean);
    const breadcrumbs = [{ name: <FaIcon name="home" />, path: '/' }];

    parts.forEach((part, index) => {
      const path = '/' + parts.slice(0, index + 1).join('/');
      breadcrumbs.push({ name: part, path });
    });

    return breadcrumbs;
  };

  if (error) {
    return (
      <div className={`file-browser ${className}`}>
        <Callout calloutClass="danger" title="Fehler">
          Fehler beim Laden der Dateien
        </Callout>
      </div>
    );
  }

  return (
    <div
      ref={browserRef}
      className={`file-browser ${className} ${isDragOver ? 'drag-over-pane' : ''}`}
      data-pane-id={paneId}
      onDragOver={handlePaneDragOver}
      onDragLeave={handlePaneDragLeave}
      onDrop={handlePaneDrop}
      onKeyDown={handleKeyDown}
      onClick={handleBrowserClick}
      tabIndex="0"
      style={{
        position: 'relative',
        outline: 'none',
        ...(isDragOver && {
          backgroundColor: dragOperation === 'copy' ? '#e7f3ff' : '#fff3cd',
          border: `2px dashed ${dragOperation === 'copy' ? '#007bff' : '#ffc107'}`,
        })
      }}
    >
      {isDragOver && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            textAlign: 'center',
            pointerEvents: 'none'
          }}
        >
          <i className={`fa ${dragOperation === 'copy' ? 'fa-copy' : 'fa-arrows'} fa-3x mb-2`} style={{ color: dragOperation === 'copy' ? '#007bff' : '#ffc107' }} />
          <div>
            <strong>{dragOperation === 'copy' ? 'Kopieren' : 'Verschieben'}</strong>
            {dragItemCount > 1 && (
              <div>
                <span className="badge badge-primary">{dragItemCount} Elemente</span>
              </div>
            )}
            <br />
            <small>{dragOperation === 'copy' ? '(Shift gedrückt)' : '(Shift = Kopieren)'}</small>
          </div>
        </div>
      )}

      <div className="browser-header">
        <div className="breadcrumb-nav">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              {getBreadcrumbs().map((crumb, index) => (
                <li
                  key={crumb.path}
                  className={`breadcrumb-item ${index === getBreadcrumbs().length - 1 ? 'active' : ''}`}
                >
                  {index === getBreadcrumbs().length - 1 ? (
                    crumb.name
                  ) : (
                    <a href="#" onClick={(e) => { e.preventDefault(); handleNavigate(crumb.path); }}>
                      {crumb.name}
                    </a>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        </div>

        <Toolbar
          currentPath={currentPath}
          selectedItems={selectedItems}
          clipboard={sharedClipboard}
          onNavigateUp={handleNavigateUp}
          onRefresh={() => handleNavigate(currentPath)}
          onUpload={() => setShowUploadModal(true)}
          onCreateFolder={() => setShowCreateModal(true)}
          onCopy={handleCopy}
          onCut={handleCut}
          onRename={handleRename}
          onChmod={handleChmod}
          onDelete={handleDelete}
          setClipboard={setSharedClipboard}
          onPaste={handlePaste}
          disabled={isLoading}
          baseApiUrl={baseApiUrl}
        />
      </div>

      {isLoading ? (
        <div className="text-center p-5">
          <FaIcon name="spinner" spin /> Lade Dateien...
        </div>
      ) : (
        <FileList
          files={sortedFiles}
          currentPath={currentPath}
          selectedItems={selectedItems}
          sortConfig={sortConfig}
          onSort={handleSort}
          onSelectItem={handleSelectItem}
          onItemClick={handleItemClick}
          onDoubleClick={handleDoubleClick}
          onDropOnItem={handleItemDrop}
          onContextMenu={handleContextMenu}
          paneId={paneId}
          renamingItem={itemToEdit}
          onRenameCancel={handleRenameCancel}
          onRenameSubmit={handleRenameSubmit}
        />
      )}

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={getContextMenuItems()}
          onClose={() => setContextMenu(null)}
        />
      )}

      {showUploadModal && (
        <UploadModal
          currentPath={uploadPath || currentPath}
          initialFiles={uploadFiles}
          baseApiUrl={baseApiUrl}
          onClose={() => {
            setShowUploadModal(false);
            setUploadFiles([]);
            setUploadPath(null);
          }}
        />
      )}

      {showCreateModal && (
        <CreateItemModal
          currentPath={currentPath}
          baseApiUrl={baseApiUrl}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {showRenameModal && itemToEdit && (
        <RenameModal
          item={itemToEdit}
          currentPath={currentPath}
          baseApiUrl={baseApiUrl}
          onClose={() => {
            setShowRenameModal(false);
            setItemToEdit(null);
          }}
        />
      )}

      {showChmodModal && itemToEdit && (
        <ChmodModal
          item={itemToEdit}
          currentPath={currentPath}
          baseApiUrl={baseApiUrl}
          onClose={() => {
            setShowChmodModal(false);
            setItemToEdit(null);
          }}
        />
      )}
    </div>
  );
};

FileBrowser.propTypes = {
  paneId: PropTypes.string.isRequired,
  className: PropTypes.string,
  sharedClipboard: PropTypes.object,
  setSharedClipboard: PropTypes.func.isRequired,
  baseApiUrl: PropTypes.string,
};

export default FileBrowser;